/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

export type FilterFieldOption = { value: string; label: string };

/** A field whose values are records found by searching: the chip shows the resolved name. */
export type FilterLookup = {
  search: (query: string) => Promise<FilterFieldOption[]>;
  resolve: (value: string) => Promise<string | null>;
};

/** A filter staff can add: a fixed choice, a date, or a record found by searching. */
export type FilterField = {
  key: string;
  label: string;
  scim: (value: string) => string;
} & (
  | { kind: "choice"; options: FilterFieldOption[]; searchable?: boolean | undefined }
  | { kind: "date" }
  | { kind: "lookup"; lookup: FilterLookup }
);

export type ActiveFilter = { key: string; value: string };

export function filtersToScim(fields: FilterField[], active: ActiveFilter[]): string | null {
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const clauses = active
    .map((filter) => {
      const field = byKey.get(filter.key);
      return field ? field.scim(escapeValue(filter.value)) : null;
    })
    .filter((clause): clause is string => clause !== null);
  return clauses.length === 0 ? null : clauses.join(" and ");
}

function escapeValue(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export function filtersFromParams(
  fields: FilterField[],
  params: URLSearchParams,
): ActiveFilter[] {
  const byKey = new Map(fields.map((field) => [field.key, field]));
  const active: ActiveFilter[] = [];
  for (const [key, value] of params) {
    const field = byKey.get(key);
    if (field && isChoosable(field, value) && !active.some((filter) => filter.key === key)) {
      active.push({ key, value });
    }
  }
  return active;
}

/** A saved link can carry a word a choice no longer offers: it is ignored, not an error. */
function isChoosable(field: FilterField, value: string): boolean {
  return value !== "" && (field.kind !== "choice" || field.options.some((option) => option.value === value));
}

export function paramsWithFilters(
  params: URLSearchParams,
  fields: FilterField[],
  active: ActiveFilter[],
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const field of fields) {
    next.delete(field.key);
  }
  for (const filter of active) {
    next.set(filter.key, filter.value);
  }
  return next;
}

export function filterValueLabel(field: FilterField, value: string): string {
  if (field.kind === "date") {
    return toDateValue(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  if (field.kind === "choice") {
    return field.options.find((option) => option.value === value)?.label ?? value;
  }
  return value;
}

export function toYmd(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function toDateValue(ymd: string): Date {
  const [year, month, day] = ymd.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function localDayStartIso(ymd: string): string {
  return toDateValue(ymd).toISOString();
}

export function localDayEndIso(ymd: string): string {
  const date = toDateValue(ymd);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}
